import './Opportunities.scss';
import { ByzzerBrandSearch } from '@/components/ByzzerBrandSearch';
import { ByzzerCategorySelect } from '@/components/ByzzerCategorySelect';
import ByzzerPPGSelect from '@/components/ByzzerPPGSelect/ByzzerPPGSelect';
import { ByzzerTable } from '@/components/ByzzerTable';
import { LimitedLabel } from '@/components/LimitedLabel';
import ByzzerSearchableSelect from '@/components/form/ByzzerSearchableSelect';
import { brandScoreMessages } from '@/constants/scorecard.constants';
import { useTenantApi } from '@/hooks';
import { OpportunityRunConfig, OpportunityTypeMenuItems } from '@/types/ScoreCard';
import { AgGridReact } from 'ag-grid-react';
import classnames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBetterNavigate } from '@/utils';
import { openErrorModal } from '@/components/form';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import { useUser } from '@/contexts/UserContext';
import { ByzColDef } from '@/types/TableTypes';
import { useEventDataWithUserInfo, useTrackEvent } from '@/analytics';
import { ByzzerButton, ByzzerSelect, ByzzerSelectValueTypes, ByzzerTextTip } from '@byzzer/ui-components';
import { DEFAULT_BYZZER_ERROR_MODAL_TITLE } from '@/components/ByzzerModal/byzzerError.constants';

export type OpportunitiesProps = {
    filters: OpportunityRunConfig;
    onFiltersChange?: (filter: OpportunityRunConfig[]) => void;
    hasScorecardAccess?: boolean;
    className?: string;
    activeTab?: string;
};

type RecommendationType = { value: Record<string, any>; key: number; opptype: string };
type OpportunityType = { display: string; value: string };

export function Opportunities({
    filters,
    onFiltersChange,
    hasScorecardAccess,
    className,
    activeTab,
    ...props
}: OpportunitiesProps) {
    const baseClassName = 'my-scorecard-opportunities';
    const { defaultRunConfig } = useUser();
    const trackEvent = useTrackEvent();
    const getEventData = useEventDataWithUserInfo();
    const [internalValue, setInternalValue] = useState<OpportunityRunConfig>(filters);
    const [categoriesToCheckForIntersect, setCategoriesToCheckForIntersect] = useState<string[]>([]);
    const { getCategoriesByBrands } = useTenantApi();
    const [opportunityType, setOpportunityType] = useState<OpportunityType | null>(
        OpportunityTypeMenuItems.find((item) => item.value === activeTab) || null
    );
    const [recommendations, setRecommendations] = useState<RecommendationType[]>([]);
    const { getBrandScoreOpportunities } = useTenantApi();
    const gridRef = useRef<AgGridReact>(null);
    const interValueRef = useRef(internalValue);
    const navigate = useBetterNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [shouldRunInitialLoad, setShouldRunInitialLoad] = useState<boolean>(false);
    const [hasSelectorsChanged, setHasSelectorsChanged] = useState<boolean>(false);
    const MAX_RECOMMENDATIONS_COUNT = 10;

    const defaultColDef = useMemo(
        () => ({
            filter: true, // make every column use 'text' filter by default
            sortable: true,
            floatingFilter: true, // enable floating filters by default
            autoHeight: false,
            wrapText: false,
            suppressMenu: true,
        }),
        []
    );

    const runNowDisabledMessage = useMemo(() => {
        const missingFields: string[] = [];

        if (!internalValue.brands?.length) {
            missingFields.push('Brand(s)');
        }
        if (!internalValue.categories?.length) {
            missingFields.push('Category(s)');
        }
        if (!internalValue.markets?.length) {
            missingFields.push('Market');
        }
        if (!opportunityType) {
            missingFields.push('Function');
        }

        if (missingFields.length === 0) {
            return '';
        }

        return `Please select ${missingFields.join(', ')} to run.`;
    }, [internalValue, opportunityType]);

    const emptyStateMessage = useMemo(() => {
        if (loading) return null;
        
        if (runNowDisabledMessage) {
            return runNowDisabledMessage;
        }
        
        if (!runNowDisabledMessage && hasSelectorsChanged) {
            return brandScoreMessages.opportunity.clickRunNowSuggestion;
        }
        
        if (!runNowDisabledMessage && !hasSelectorsChanged && recommendations.length === 0) {
            return brandScoreMessages.opportunity.noOpportunities;
        }
        
        return null;
    }, [loading, runNowDisabledMessage, hasSelectorsChanged, recommendations.length]);

    useEffect(() => {
        initializeDefaultFilters();
    }, [filters]);

    useEffect(() => {
        interValueRef.current = internalValue;
    }, [internalValue]);

    useEffect(() => {
        (async () => {
            const { brands } = internalValue;
            const categories = await Promise.all([brands?.length ? await getCategoriesByBrands(brands) : []]);
            setCategoriesToCheckForIntersect(categories.flat());
        })();
    }, [internalValue.brands]);

    useEffect(() => {
        if (!shouldRunInitialLoad) return;

        if (
            Boolean(internalValue.brands?.length && internalValue.categories?.length && internalValue.markets?.length)
        ) {
            loadOpportunities(opportunityType?.value);
        } else {
            // alert('please select all the mandatory values') //TODO:implement alert
            setRecommendations([]);
        }

        if (shouldRunInitialLoad) {
            setShouldRunInitialLoad(false);
        }
    }, [internalValue, shouldRunInitialLoad]);

    const initializeDefaultFilters = () => {
        if (Array.isArray(filters.markets)) {
            const market =
                hasScorecardAccess === true
                    ? filters?.markets?.[0] || defaultRunConfig?.rms?.markets?.[0]?.name
                    : 'Total FMCG Retailers';
            const updatedFilters = { ...filters, markets: market?.length ? [market as string] : [], ppgId: -1 };
            setInternalValue(updatedFilters);
        } else {
            setInternalValue(filters);
        }
        setShouldRunInitialLoad(true);
    };

    const handleFilterChange = (event) => {
        const { name, value } = event;
        if (!name) {
            return;
        }

        if (name === 'categories' && hasScorecardAccess === true) {
            //dont clear the market selector always, BYZ-12251
            setInternalValue({ ...internalValue, markets: undefined });
        }

        setInternalValue((prevState) => ({
            ...prevState,
            [name]: value,
        }));
        setHasSelectorsChanged(true);
        setRecommendations([]);
    };

    const handleMarketSearchChange = async (market) => {
        setInternalValue({
            ...internalValue,
            markets: market.length ? [market] : [],
            ppgId: internalValue?.ppgId ?? -1,
        });
        setHasSelectorsChanged(true);
        setRecommendations([]);
    };

    const handleOpportunityChange = (event: ByzzerChangeEvent<ByzzerSelectValueTypes | undefined>) => {
        setOpportunityType(event.data);
        setHasSelectorsChanged(true);
        setRecommendations([]);
    };

    const handleSyncWithDefaults = () => {
        initializeDefaultFilters();
        setOpportunityType(OpportunityTypeMenuItems.find((item) => item.value === activeTab) || null);
        setHasSelectorsChanged(false);
    };

    const handleRunNow = () => {
        if (
            Boolean(internalValue.brands?.length && internalValue.categories?.length && internalValue.markets?.length)
        ) {
            loadOpportunities(opportunityType?.value);
        } else {
            // alert('please select all the mandatory values') //TODO:implement alert
            setRecommendations([]);
        }
        setHasSelectorsChanged(false);
    };

    async function loadOpportunities(type: string | undefined) {
        try {
            if (!type) return;

            let allSelection = {
                brands: internalValue.brands,
                categories: internalValue.categories,
                markets: internalValue.markets?.length ? [internalValue?.markets?.[0]] : [],
                ppgIds: ['assortment', 'product'].includes(type)
                    ? undefined
                    : internalValue?.ppgId
                    ? [internalValue.ppgId]
                    : undefined,
            };

            if (Boolean(internalValue?.markets?.length)) {
                setLoading(true);
                const recommendations = await getBrandScoreOpportunities(allSelection, type);

                setRecommendations(
                    recommendations
                        .slice(0, MAX_RECOMMENDATIONS_COUNT)
                        .map((opportunity, index) => ({ value: opportunity, key: index + 1, opptype: type }))
                );
            }
        } catch (error: any) {
            openErrorModal({
                title: DEFAULT_BYZZER_ERROR_MODAL_TITLE,
                errorId: error.id,
            });
        } finally {
            setLoading(false);
        }
    }

    //internalValue doesn't give the latest values,so am using interValueRef below
    function loadReport(item: any) {
        trackEvent({
            type: 'click',
            name: `brand_score_top_opps_report_nav_${opportunityType?.display.toLowerCase().replace(' ', '_')}`,
            data: getEventData({ opportunityType: opportunityType?.display }),
        });
        navigate(`/scorecard/${item.data.opptype}_action_plan`, {
            state: {
                focusBrands: interValueRef.current.brands,
                focusCategories: interValueRef.current.categories,
                markets: interValueRef.current.markets,
                activeLever: item.data.opptype,
                currentLeverSku: `${item.data.opptype.toLowerCase()}_action_plan`,
                ppgId: ['assortment', 'product'].includes(item.data.opptype) ? undefined : interValueRef.current.ppgId,
                sortingField: ['assortment', 'product'].includes(item.data.opptype) ? 'UPC' : 'PPG',
                sortingFieldValue: ['assortment', 'product'].includes(item.data.opptype)
                    ? item.data.value.upc
                    : item.data.value.ppg,
            },
        });
    }

    const columnDefs = useMemo<ByzColDef<RecommendationType>[]>(
        () => [
            {
                headerName: '',
                field: 'value.opportunity',
                flex: 1,
                wrapText: true,
                autoHeight: true,
                // cellRenderer: ({value}) => <span className={`${baseClassName}__cell-content`}>{value.opportunity}</span>,
                valueGetter: ({ data }) => {
                    return `${data?.value?.opportunity}`;
                },
            },
            {
                filter: false,
                sortable: false,
                floatingFilter: false,
                hide: !hasScorecardAccess,
                cellRenderer: (item) => {
                    return (
                        <a className={`${baseClassName}__actions`} onClick={() => loadReport(item)}>
                            &#8594;
                        </a>
                    );
                },
                width: 80,
            },
        ],
        [hasScorecardAccess, loadReport]
    );

    return (
        <div className={classnames(`${baseClassName}`)}>
            <header className={classnames(`${baseClassName}__header`)}>
                <span>{brandScoreMessages.opportunity.headerMessage}</span>
            </header>

            <main className={classnames(`${baseClassName}__selectors`)}>
                <ByzzerBrandSearch
                    className={`${baseClassName}__selectors--selector`}
                    name={'brands'}
                    label={<LimitedLabel label="Brand(s) :" />}
                    value={internalValue?.brands}
                    onChange={handleFilterChange}
                />
                <ByzzerCategorySelect
                    className={`${baseClassName}__selectors--selector`}
                    name={'categories'}
                    value={internalValue?.categories}
                    onChange={handleFilterChange}
                    placeholder={'Select from the list'}
                    label={<LimitedLabel label="Category(s) :" />}
                    categoriesToCheckForIntersect={categoriesToCheckForIntersect}
                    shouldDisplayIntersectIndicators={internalValue?.brands?.length! > 0 || false}
                    categorySelectionAggregationLevel={'category'}
                />

                {['Assortment', 'New Products'].includes(opportunityType?.display!) ? (
                    <>
                        <div className={`${baseClassName}__selectors--selector`}>
                            <ByzzerTextTip
                                tipLocation="bottom-end"
                                tip={`PPGs do not apply to the ${opportunityType?.display} function.`}
                            >
                                <span>
                                    <ByzzerPPGSelect
                                        className={`${baseClassName}__selectors--selector`}
                                        name={'ppgId'}
                                        label={'PPG Definition :'}
                                        // onChange={handleFilterChange}
                                        value={internalValue?.ppgId ?? -1}
                                        categories={internalValue?.categories}
                                        allowClear={true}
                                        dontUseDefault={true}
                                        showDescription={false}
                                        disabled={['Assortment', 'New Products'].includes(opportunityType?.display!)}
                                    />
                                </span>
                            </ByzzerTextTip>
                        </div>
                    </>
                ) : (
                    <ByzzerPPGSelect
                        className={`${baseClassName}__selectors--selector`}
                        name={'ppgId'}
                        label={<LimitedLabel label="PPG Definition :" />}
                        onChange={handleFilterChange}
                        value={internalValue?.ppgId ?? -1}
                        categories={internalValue?.categories}
                        allowClear={true}
                        dontUseDefault={true}
                        showDescription={false}
                        disabled={['Assortment', 'New Products'].includes(opportunityType?.display!)}
                    />
                )}

                <ByzzerSearchableSelect
                    name="markets"
                    className={`${baseClassName}__selectors--selector`}
                    disabled={!hasScorecardAccess}
                    value={internalValue?.markets ?? []}
                    placeholder="Search for Market"
                    label={<LimitedLabel label="Market :" />}
                    options={internalValue?.markets ? internalValue?.markets : []}
                    searchPlaceholder="Search for Market"
                    onChange={handleFilterChange}
                    searchOnChange={handleMarketSearchChange}
                    searchType="marketSingleSearch"
                    categories={internalValue?.categories ?? []}
                    searchValue={internalValue?.markets ? internalValue?.markets : undefined}
                    optionKey={undefined}
                    seperator={undefined}
                    selectedMarkets={undefined}
                    productType={undefined}
                    multiBrandSelect={undefined}
                    fromFilter={true}
                />

                <ByzzerSelect
                    className={`${baseClassName}__selectors--selector`}
                    name="function"
                    label="Function :"
                    value={opportunityType?.value}
                    options={OpportunityTypeMenuItems}
                    onChange={handleOpportunityChange}
                    allowMultiple={false}
                />

                <div className={`${baseClassName}__selectors--actions`}>
                    <ByzzerButton
                        disabled={!!runNowDisabledMessage}
                        disabledTip={runNowDisabledMessage}
                        onClick={handleRunNow}
                        label={'Run Now'}
                    />
                    <ByzzerButton type={'negative'} onClick={handleSyncWithDefaults} label={'Sync With Defaults'} />
                </div>
            </main>

            <div className={classnames(`${baseClassName}__midselectors`)}>
                <header className={classnames(`${baseClassName}__header`)}>
                    <span>
                        <b>Opportunities</b>
                    </span>
                </header>
            </div>
            <ByzzerMask show={loading} loading={loading} />
            {recommendations && recommendations.length > 0 && (
                <ByzzerTable
                    ref={gridRef} // Ref for accessing Grid's API
                    rowData={recommendations} // Row Data for Rows
                    columnDefs={columnDefs} // Column Defs for Columns
                    defaultColDef={defaultColDef} // Column Defs for Columns
                />
            )}

            {emptyStateMessage && (
                <div className={`${baseClassName}__emptygrid`}>
                    <p>{emptyStateMessage}</p>
                </div>  
            )}
        </div>
    );
}
